/**
 * Is that surface an overbounce?
 *
 * Copyright (C) 2026 Overbounce contributors
 * Licensed under the GNU General Public License v2 or later. See LICENSE.
 *
 * DeFRaG has an overbounce detector on its HUD, and it is the single most
 * useful readout the mod has: overbounce spots are invisible. Nothing about a
 * floor tells you that landing on it from one particular height converts your
 * fall into horizontal speed. Players learn the spots by memorising maps.
 *
 * This answers the question directly, and it answers it by SIMULATING rather
 * than by a formula. That matters. The condition is decided by sub-unit
 * position after `SnapVector` on the landing frame, so any closed form is an
 * approximation of the real physics, and an approximation is exactly what this
 * project must not ship. `Simulation` is the authority; asking it is cheap.
 *
 * Two facts make the simulation valid as a general answer, and both are pinned
 * by `test/physics/ob-heights.test.ts`:
 *
 *  1. Which drops overbounce depends only on the DROP HEIGHT, not on where the
 *     floor is. A floor at z=0 and one at z=-2048.25 give the same set. So a
 *     synthetic flat floor stands in for the real surface.
 *  2. The heights fall in narrow bands, one frame of falling apart. This is why
 *     the answer flips as you edge forward: you are moving between bands.
 *
 * What it does NOT model is a sloped surface. Overbounce is `PM_WalkMove`
 * clipping the velocity against the ground plane, so the plane's normal is part
 * of the mechanic, and a ramp is a different question. Callers pass the surface
 * normal and get `null` for anything that is not flat.
 */

import { axialBrush } from '../collision/brush.js';
import { brushListModel } from '../collision/model.js';
import type { CollisionModel } from '../collision/model.js';
import { CONTENTS_SOLID, JUMP_VELOCITY } from '../physics/constants.js';
import { Simulation } from '../physics/simulate.js';

/**
 * How an overbounce would be reached, using DeFRaG's own detector letters.
 *
 * DeFRaG is closed source, so these are community-documented rather than
 * ported -- the same standing as CPM physics, and described the same way. The
 * letters and their meanings are the mod's; which spots get which letter is
 * computed from this project's own physics, by simulating.
 *
 *   G  walk off the ledge you are standing on, no jump
 *   J  jump off the ledge
 *   B  not a bounce type: you are midair (or sticky) and one is right below
 *   p  fire the plasma gun at your feet, no jump
 *   P  plasma hop -- fire at your feet, jump, ride it to the apex, then fall
 *   r  fire the rocket launcher at your feet, no jump
 *   R  rocket jump -- fire at your feet, jump, ride it up, then fall
 *
 * with two combinable modifiers:
 *
 *   s  sticky -- a persistent minibounce, the player floating under a unit
 *      above the floor. Jumping from it lands the overbounce on the next touch
 *      at the same height.
 *   q  needs Quad, which triples the self-damage and so the launch velocity
 */
export const enum ObMethod {
  NONE = 0,
  GO = 1,
  JUMP = 2,
  PLASMA = 3,
  PLASMA_HOP = 4,
  ROCKET = 5,
  ROCKET_JUMP = 6,
  BELOW = 7,
}

export const OB_LETTER: Record<ObMethod, string> = {
  [ObMethod.NONE]: '',
  [ObMethod.GO]: 'G',
  [ObMethod.JUMP]: 'J',
  [ObMethod.PLASMA]: 'p',
  [ObMethod.PLASMA_HOP]: 'P',
  [ObMethod.ROCKET]: 'r',
  [ObMethod.ROCKET_JUMP]: 'R',
  [ObMethod.BELOW]: 'B',
};

export interface ObResult {
  method: ObMethod;
  /** Needs Quad: the plain launch does not reach a band but the tripled one does. */
  quad: boolean;
  /** The player is currently in the sticky minibounce state. */
  sticky: boolean;
  /** Drop from the player's origin to the surface, in units. */
  height: number;
}

/** The full readout, e.g. `sR` or `qP`. Empty when there is nothing to show. */
export function obLabel(result: ObResult): string {
  if (result.method === ObMethod.NONE) {
    return '';
  }
  return (
    (result.sticky ? 's' : '') + (result.quad ? 'q' : '') + OB_LETTER[result.method]
  );
}

/**
 * Vertical velocity a point-blank shot at your own feet adds.
 *
 * Measured from this project's own `radiusDamage` rather than derived on paper
 * -- see `tools/diag/self-launch.ts`. The rocket's quad figure is capped, not
 * tripled: `kvel = dir * min(damage, MAX_KNOCKBACK) * 5`, and 300 clamps to
 * 200, so a quad rocket gives 1000 rather than 1500.
 */
export const PLASMA_LAUNCH = 75;
export const PLASMA_LAUNCH_QUAD = 225;
export const ROCKET_LAUNCH = 500;
export const ROCKET_LAUNCH_QUAD = 1000;

/**
 * A surface flat enough for the question to mean anything.
 *
 * `MIN_WALK_NORMAL` is 0.7 -- the steepest thing Quake calls ground -- but a
 * 45-degree ramp is not what the detector is about, and answering with a flat
 * floor's table would be wrong rather than merely unhelpful.
 */
const FLAT_NORMAL_Z = 0.999;

/** Horizontal speed the probe carries in. Overbounce needs some; see below. */
const PROBE_SPEED = 100;

/**
 * `PM_WalkMove` bails before the rescale when there is no horizontal velocity:
 *
 *     if ( !pm->ps->velocity[0] && !pm->ps->velocity[1] ) return;
 *
 * so a dead-vertical drop has nothing to convert. The probe carries 100ups for
 * the same reason a player has to be moving to use a spot at all.
 *
 * An overbounce roughly preserves the total speed, so a 200-unit fall arriving
 * at ~500ups leaves ~500ups horizontal. Anything past 1.5x the speed it came
 * in with is unambiguous -- an ordinary landing only ever loses speed.
 */
const OB_SPEED_RATIO = 1.5;

/** Ticks to run past first ground contact before deciding. */
const SETTLE_TICKS = 4;

/** Give up on a drop that has not landed. 4s at 8ms is a 78,000 unit fall. */
const MAX_TICKS = 500;

/**
 * The floor is 512 thick and 16k wide: wide enough that the probe's horizontal
 * drift never runs off it, thick enough never to be fallen through.
 */
function probeWorld(): CollisionModel {
  return brushListModel([
    axialBrush([-8192, -8192, -512], [8192, 8192, 0], CONTENTS_SOLID),
  ]);
}

/** Built once. It has no state, and rebuilding it per query is the whole cost. */
let world: CollisionModel | null = null;

/**
 * Run one drop and report whether it overbounced.
 *
 * `dropHeight` is measured between the player's ORIGIN and where the origin
 * would rest on the target floor, which is the number the caller can compute
 * without knowing anything about bounding boxes.
 */
function probe(dropHeight: number, initialVelocityZ: number): boolean {
  if (!(dropHeight > 0) || !Number.isFinite(dropHeight)) {
    return false;
  }
  world ??= probeWorld();

  const sim = new Simulation({
    world,
    // The floor is at z=0 and a resting player's origin sits 24 above it.
    origin: [0, 0, 24 + dropHeight],
    velocity: [PROBE_SPEED, 0, initialVelocityZ],
  });

  let peak = PROBE_SPEED;
  let grounded = 0;
  for (let i = 0; i < MAX_TICKS; i++) {
    sim.step({});
    peak = Math.max(peak, sim.speed);
    if (sim.onGround) {
      if (++grounded > SETTLE_TICKS) {
        break;
      }
    } else {
      grounded = 0;
    }
  }

  return peak > PROBE_SPEED * OB_SPEED_RATIO;
}

/**
 * Memoised by drop height and launch velocity.
 *
 * Height is quantised to 1/16 of a unit: finer than the bands are wide, and
 * coarse enough that a player standing still does not re-run the probe every
 * frame as their origin jitters in the last decimal. Velocity is already a
 * whole number -- `SnapVector` rounds it every tick -- so the key space stays
 * small even though this runs on every frame of every fall.
 */
const cache = new Map<string, boolean>();

/** Bound on the cache. A long session aiming around a map is thousands of keys. */
const CACHE_LIMIT = 8192;

function cachedProbe(heightKey: number, velocityZ: number): boolean {
  const key = `${heightKey}:${velocityZ}`;
  const hit = cache.get(key);
  if (hit !== undefined) {
    return hit;
  }
  const answer = probe(heightKey / 16, velocityZ);
  if (cache.size >= CACHE_LIMIT) {
    cache.clear();
  }
  cache.set(key, answer);
  return answer;
}

/**
 * The methods, in the order a player would rather use them.
 *
 * Cheapest first: walking off costs nothing, jumping costs nothing, plasma
 * costs a little health, a rocket costs a lot. Reporting the first that works
 * means the letter is always the easiest way in, which is the only ordering
 * that makes the readout actionable.
 *
 * `BELOW` is not here. It is situational rather than chosen -- see
 * `overbounceBelow`.
 */
const METHODS: readonly { method: ObMethod; launch: (quad: boolean) => number }[] = [
  { method: ObMethod.GO, launch: () => 0 },
  { method: ObMethod.JUMP, launch: () => JUMP_VELOCITY },
  { method: ObMethod.PLASMA, launch: (q) => (q ? PLASMA_LAUNCH_QUAD : PLASMA_LAUNCH) },
  {
    method: ObMethod.PLASMA_HOP,
    launch: (q) => JUMP_VELOCITY + (q ? PLASMA_LAUNCH_QUAD : PLASMA_LAUNCH),
  },
  { method: ObMethod.ROCKET, launch: (q) => (q ? ROCKET_LAUNCH_QUAD : ROCKET_LAUNCH) },
  {
    method: ObMethod.ROCKET_JUMP,
    launch: (q) => JUMP_VELOCITY + (q ? ROCKET_LAUNCH_QUAD : ROCKET_LAUNCH),
  },
];

/** Methods whose launch velocity does not change with Quad — no point retrying. */
function variesWithQuad(method: ObMethod): boolean {
  return method !== ObMethod.GO && method !== ObMethod.JUMP;
}

export interface ObOptions {
  /** The player is in the sticky minibounce state; shows as `s`. */
  sticky?: boolean;
  /** Quad is running, so the quad-only bounces are actually available. */
  hasQuad?: boolean;
}

const NOTHING: ObResult = {
  method: ObMethod.NONE,
  quad: false,
  sticky: false,
  height: 0,
};

/**
 * Which method, if any, reaches an overbounce on the surface at `surfaceZ`.
 *
 * `surfaceNormalZ` gates the whole question: overbounce is `PM_WalkMove`
 * clipping the velocity against the ground plane, so a ramp is a different
 * problem and gets no answer rather than a wrong one.
 *
 * Quad is searched last and only for the weapon methods, so a spot reachable
 * without it never gets labelled `q`.
 */
export function classifyOverbounce(
  originZ: number,
  surfaceZ: number,
  surfaceNormalZ: number,
  options: ObOptions = {},
): ObResult {
  if (surfaceNormalZ < FLAT_NORMAL_Z) {
    return NOTHING;
  }

  // Where the origin would come to rest, so the caller's number and the
  // probe's are the same quantity.
  const dropHeight = originZ - (surfaceZ + 24);
  const heightKey = Math.round(dropHeight * 16);
  const sticky = options.sticky ?? false;

  for (const quad of options.hasQuad ? [false, true] : [false]) {
    for (const entry of METHODS) {
      if (quad && !variesWithQuad(entry.method)) {
        continue;
      }
      if (cachedProbe(heightKey, entry.launch(quad))) {
        return { method: entry.method, quad, sticky, height: dropHeight };
      }
    }
  }

  return { ...NOTHING, sticky, height: dropHeight };
}

/**
 * `B` -- the fall already in progress lands on an overbounce.
 *
 * A different question from `classifyOverbounce`, and a more exact one: it
 * takes the player's real height and real vertical velocity and runs the fall
 * from there, so the answer accounts for where in the frame the fall happens to
 * be. A standing prediction cannot know that.
 *
 * DeFRaG describes `B` as "not really a bounce type" for a good reason -- it is
 * not something you set up, it is something you notice. What it demands is an
 * input: `PM_WalkMove` converts nothing without horizontal velocity, so falling
 * dead straight onto the spot wastes it.
 */
export function overbounceBelow(
  originZ: number,
  surfaceZ: number,
  surfaceNormalZ: number,
  velocityZ: number,
  options: ObOptions = {},
): ObResult {
  const sticky = options.sticky ?? false;
  if (surfaceNormalZ < FLAT_NORMAL_Z || velocityZ >= 0) {
    return { ...NOTHING, sticky };
  }

  const dropHeight = originZ - (surfaceZ + 24);
  const heightKey = Math.round(dropHeight * 16);
  if (!cachedProbe(heightKey, Math.round(velocityZ))) {
    return { ...NOTHING, sticky, height: dropHeight };
  }
  return { method: ObMethod.BELOW, quad: false, sticky, height: dropHeight };
}

/**
 * The sticky minibounce: "the player floats less than 1u over the ground".
 *
 * It is the resting fixed point documented in CLAUDE.md -- OVERCLIP leaves a
 * residual of `-0.001 * vz`, SnapVector rounds it to a small positive integer,
 * and `PM_WalkMove` regenerates it every frame. A player who landed at -558ups
 * sits at `vz = 1` forever, a hair above the floor. Jumping out of that state
 * lands the overbounce on the next touch at the same height, which is why
 * defrag runners set it up deliberately at the start of a strafe.
 */
export function isSticky(velocityZ: number, onGround: boolean): boolean {
  return onGround && velocityZ > 0;
}

/** Drop the memo. Only useful in tests, where the probe world is rebuilt. */
export function resetOverbounceCache(): void {
  cache.clear();
  world = null;
}

/**
 * Holds one fall's `B` answer still.
 *
 * `overbounceBelow` is asked every frame, and while a player is falling its
 * answer flickers: the bands are about a quarter of a unit wide, the drop
 * height is quantized into the probe's cache key, and the fall sweeps through
 * band after band on the way down. The readout strobes, which is worse than
 * useless -- it is the one readout a player is supposed to trust at a glance.
 *
 * The fix is not a timer. A fall's outcome is DECIDED: from a given height and
 * vertical velocity, where the landing tick puts the player is already fixed,
 * so the honest reading is "this fall will overbounce", latched from the first
 * frame that says so until the fall ends. Three things end it:
 *
 *  - landing, which is the answer arriving;
 *  - vertical velocity going non-negative, which is a NEW fall (a rocket, a
 *    jump pad, the top of an arc) and has to be answered again;
 *  - the surface below changing, because the player moved horizontally over a
 *    different floor and the old answer was about the old floor.
 *
 * A latch only ever holds a positive. A fall that has not yet found an
 * overbounce keeps asking, so one detected late still shows up.
 */
export class ObFallLatch {
  private held: ObResult | null = null;
  private heldSurfaceZ = 0;

  /**
   * `surfaceZ` is the plane the fall is heading for, or null when there is no
   * surface below within probing distance. `probe` is only called when the
   * latch has nothing to offer, so a held answer costs no simulation at all.
   */
  update(
    onGround: boolean,
    velocityZ: number,
    surfaceZ: number | null,
    probe: () => ObResult,
  ): ObResult | null {
    if (onGround || velocityZ >= 0 || surfaceZ === null) {
      this.held = null;
      return null;
    }
    // A different floor is a different question. The tolerance is well under
    // the width of a band, so this cannot swallow a real change of surface
    // while still ignoring the epsilon a box trace leaves behind.
    if (this.held && Math.abs(surfaceZ - this.heldSurfaceZ) > 0.01) {
      this.held = null;
    }
    if (this.held) {
      return this.held;
    }
    const answer = probe();
    if (answer.method !== ObMethod.NONE) {
      this.held = answer;
      this.heldSurfaceZ = surfaceZ;
      return answer;
    }
    return null;
  }

  /** For a course restart, where the player is put back without landing. */
  reset(): void {
    this.held = null;
  }
}

/**
 * The margin a speed rise must clear to be an overbounce, in units per second.
 *
 * DERIVED, not tuned. `PM_Accelerate` can add at most
 * `pm_accelerate * wishspeed * frametime` in one tick, and `wishspeed` is
 * capped at `ps.speed`: 10 * 320 * 0.008 = 25.6, or 33.3 with Haste's 1.3x.
 * CPM is the worse case and still fits -- `CPM_ACCELERATE` is 15, so
 * 15 * 320 * 1.3 * 0.008 = 49.9. Rounded up past both gives 64.
 *
 * Measured against the simulation rather than taken on faith: a 420-drop
 * sweep on flat ground put the largest one-tick rise from ordinary ground
 * acceleration at 26.0, and the SMALLEST real overbounce spike at 283. The
 * two populations do not come close to touching, which is what makes a
 * threshold honest here rather than a knob.
 *
 * The shipped value was 10, which sat *under* the acceleration ceiling. See
 * `.agent/docs/own-sfx.md`.
 */
export const OB_SPEED_MARGIN = 64;

/**
 * How many observations after touching down an overbounce may still arrive.
 *
 * **This is the part that is easy to get wrong, and shipped wrong.** An
 * overbounce is not produced by the tick that lands: landing registers at the
 * end of tick N, `PM_GroundTrace` leaves `velocity[2]` alone (id's zeroing
 * line is commented out), and `PM_WalkMove` converts the retained fall speed
 * on tick **N+1**, by which time the player has already been on the ground for
 * a tick. `test/physics/overbounce.test.ts`'s own header says so in prose, and
 * a sweep of 293 overbouncing drop heights put the spike on grounded tick 2
 * every single time, never tick 1.
 *
 * So a test of the form "airborne last tick, grounded this one, and faster"
 * describes a landing that *gained speed while landing* -- ordinary ground
 * acceleration -- and never describes an overbounce at all. Two is the window
 * that catches the real thing; it is not slack.
 */
export const OB_LANDING_TICKS = 2;

/**
 * How far `velocity[2]` must swing, in units per second, for the VERTICAL arm.
 *
 * A grounded player's vertical velocity is never far from zero by any ordinary
 * route: `OVERCLIP` leaves a residual of `-0.001 * vz` -- about 1 for a
 * 1000ups impact -- `SnapVector` rounds it to an integer, and the sticky
 * minibounce sits at a unit or two (`isSticky`). A vertical overbounce is
 * three orders louder than that: the smallest launch in a 20-to-600 sweep was
 * **174**, and the bounce is near-perfectly elastic, so the launch matches the
 * impact.
 *
 * 10 is the same figure `test/physics/overbounce.test.ts`'s own vertical-drop
 * helper uses to spot one. It sits 10x above the residual and 17x below the
 * quietest real bounce; across 961,200 ticks of jumping, strafe-jumping,
 * bunny-hopping and landing from every height, nothing but an overbounce ever
 * matched the pattern.
 */
export const OB_BOUNCE_VZ = 10;

/**
 * The overbounce, over a stream of observations -- a live run's ticks, a
 * ghost's re-simulated ticks, the snapshots of a demo.
 *
 * ## One mechanic, two things to watch for
 *
 * `PM_WalkMove` measures full velocity magnitude, clips it flat against the
 * ground plane, renormalises and rescales to that magnitude, so the vertical
 * component `PM_GroundTrace` deliberately did not zero comes back somewhere
 * else. WHERE it comes back depends on how much horizontal velocity was there
 * to point it at, and the two answers look nothing alike from outside:
 *
 *  - **Horizontal** (`HOB`). With horizontal velocity, the rescaled vector is
 *    almost all horizontal: the player keeps running, hundreds of ups faster.
 *    Seen as a speed that jumps, on the ground, a tick or two after touchdown.
 *  - **Vertical** (`VOB`). With none, clipping leaves only OVERCLIP's tiny
 *    upward residual; normalising that gives exactly (0,0,1) and scaling it by
 *    the full magnitude launches the player straight back up at the speed they
 *    landed at. Seen as `velocity[2]` flipping from far negative to far
 *    positive in one tick, off a grounded tick.
 *
 * **The vertical one is what Quake 3 players mean by "an OB"** -- it is the
 * elastic bounce that reaches otherwise unreachable places, it is what
 * `ob_basics` teaches ("LET GO of every key! 0 ups means you bounce straight
 * back up"), and a detector that watched horizontal speed alone would be
 * silent through the whole of that lesson. Both arms are the same four lines
 * of `PM_WalkMove`; neither is a special case of the other.
 *
 * Horizontal speed for the first arm, never full magnitude: magnitude is
 * precisely the quantity an overbounce conserves, so measuring it would make
 * every landing look like one.
 *
 * ## What it must be fed, and what it must not
 *
 * Pmove's OWN post-tick velocity, paired with pmove's own `onGround`.
 *
 * `GameFrame.speed`/`.velocity` are not that: they are re-read at the end of
 * `Game.step`, after `touchJumpPad` may have SET the velocity, a teleporter
 * replaced it, knockback added to it or a respawn zeroed it -- while
 * `.onGround` stays pmove's. Landing on a jump pad then reads as
 * `onGround: true, vz -246 -> +711` on one frame, which is an overbounce's
 * exact shape and is nothing but the pad. `GameFrame.pmoveSpeed` and
 * `.pmoveVelocityZ` exist for this, and `.agent/docs/own-sfx.md` §5 has the
 * measurements.
 *
 * Demo playback is the exception and knowingly so: a snapshot is the wire's
 * post-everything velocity and there is no pmove-side pair to read. What that
 * costs is measured in the same doc.
 *
 * ## No baseline until the first observation
 *
 * The point, not an initialisation detail. A watch that assumed "airborne, at
 * rest" would fire on the first observation after every reset, and in playback
 * a reset is what a seek does -- so dropping the scrubber anywhere would
 * invent an overbounce out of the first tick it landed on. A fresh watch seeds
 * and says no.
 */
/** Why `ObLandingWatch` fired -- see its `fire` field. */
export interface ObFire {
  arm: 'horizontal' | 'vertical';
  /** Observations on the ground including this one; 0 while airborne. */
  groundedFor: number;
  speedBefore: number;
  speed: number;
  velocityZBefore: number;
  velocityZ: number;
}

export class ObLandingWatch {
  /**
   * What the last `true` was made of, for whoever wants to say so out loud.
   *
   * Two rounds of "the sting fires when nothing happened" were spent
   * speculating about numbers nobody had printed -- the same way two rounds
   * went on the bullet flash's frame rate (`.agent/docs/bullet-flash-rate.md`).
   * The caller holds none of this: the previous observation lives in here.
   * So it is offered rather than reconstructed.
   *
   * Null until the first fire, and rewritten on every one. Read it only
   * immediately after `observe` returns true.
   */
  fire: ObFire | null = null;

  /**
   * Observations spent on the ground, this touchdown. 0 while airborne, 1 on
   * the tick that lands, 2 on the tick a horizontal overbounce converts.
   */
  private groundedFor = 0;
  private speed = 0;
  private velocityZ = 0;
  private onGround = false;
  private seeded = false;

  /**
   * True exactly on the observation an overbounce arrived on.
   *
   * `velocityZ` is optional so a caller with only a speed can still drive the
   * horizontal arm -- but a caller that CAN pass it should, or it will hear
   * nothing at the one spot most players would call the real thing.
   */
  observe(onGround: boolean, speed: number, velocityZ = 0): boolean {
    const wasSeeded = this.seeded;
    const previousSpeed = this.speed;
    const previousVz = this.velocityZ;
    const wasOnGround = this.onGround;

    this.groundedFor = onGround ? this.groundedFor + 1 : 0;
    this.speed = speed;
    this.velocityZ = velocityZ;
    this.onGround = onGround;
    this.seeded = true;
    if (!wasSeeded) {
      return false;
    }

    const record = (arm: 'horizontal' | 'vertical'): true => {
      this.fire = {
        arm,
        groundedFor: this.groundedFor,
        speedBefore: previousSpeed,
        speed,
        velocityZBefore: previousVz,
        velocityZ: velocityZ,
      };
      return true;
    };

    // HOB: a speed that jumped, on the ground, within the landing window.
    if (
      this.groundedFor >= 1 &&
      this.groundedFor <= OB_LANDING_TICKS &&
      speed > previousSpeed + OB_SPEED_MARGIN
    ) {
      return record('horizontal');
    }

    /*
     * VOB: the elastic bounce. Off a GROUNDED observation, which is what
     * separates it from everything else that reverses a falling player --
     * a jump pad or a mover catches you in the air, and `trigger_push` fires
     * on a tick you were not standing on anything.
     */
    if (wasOnGround && previousVz < -OB_BOUNCE_VZ && velocityZ > OB_BOUNCE_VZ) {
      return record('vertical');
    }
    return false;
  }

  /** Forget the baseline: a seek in playback, or any restart of the stream. */
  reset(): void {
    this.fire = null;
    this.groundedFor = 0;
    this.speed = 0;
    this.velocityZ = 0;
    this.onGround = false;
    this.seeded = false;
  }
}
